// @flow
import React, { PureComponent } from 'react';
import { Redirect } from 'react-router-dom';
import { withHelmet } from 'infrastructure/seo/Helmet';
import Breadcrumb from 'components/fragments/Breadcrumb';
import JoinStories from 'components/fragments/JoinStories';
import ArticleHeader from 'components/newsletter/ArticleHeader';
import ContentBloc from 'components/newsletter/blocs/ContentBloc';
import ArticleSocial from 'components/newsletter/ArticleSocial';
import ArticleTags from 'components/newsletter/ArticleTags';
import SimilarContent from 'components/newsletter/SimilarContent';
import FooterSocial from 'components/footer/FooterSocial';
import { makeAmpLink } from 'constants/app';
import Sidebar from 'components/sidebar/Sidebar';
import { getDateFromString } from 'utils/dateUtils';
import type { ArticleType } from 'types/Article';
import type { Status } from 'types/Status';
import type { BlocContent } from 'types/NewsletterBloc';
import type { FullUserType } from 'types/User';
import { handleTagEvent } from 'utils/tagManagerUtils';
import PageNotFound from 'components/app/PageNotFound';
import { STATUS_UNKNOWN, STATUS_SUCCESS } from 'constants/statusConstants';

export type OwnProps = {
  match: {
    params: {
      slug: string
    }
  },
  location: {
    pathname: string,
    search: string
  }
};

export type StateProps = {
  article: ArticleType,
  articles: Array<ArticleType>,
  status: Status,
  userPref: FullUserType
};

export type DispatchProps = {
  fetchArticle: (slug: string) => void,
  fetchArticlePreview: (id: number) => void,
  articleFetched: (article: ArticleType) => void
};

type State = {
  shouldDisplayLoading: boolean
};

type HistoryProps = {
  history: any
};

type Props = OwnProps & StateProps & DispatchProps & HistoryProps;

class Article extends PureComponent<Props, State> {
  props: Props;
  _links;
  _embedTweet;
  _embedFacebook;
  _embedInstagram;
  _articleContent;

  componentDidMount() {
    const cachedArticle = this.props.articles.find(article => article.slug === this.props.match.params.slug);
    if (cachedArticle) {
      this.props.articleFetched(cachedArticle);
    }
    if (this.props.location.search && /\?p=/.test(this.props.location.search)) {
      let id = this.props.location.search;
      id = id.replace('?p=', '');
      this.props.fetchArticlePreview(parseInt(id, 10));
    } else {
      this.props.fetchArticle(this.props.match.params.slug);
    }
    const {
      status,
      article: { redirection }
    } = this.props;
    if (status === STATUS_SUCCESS && redirection) {
      return <Redirect to={{ pathname: redirection }} />;
    }
    return null;
  }

  componentWillUnmount() {
    const { userPref } = this.props;
    if (this._links) {
      this._links.forEach(link => {
        link.removeEventListener(
          'click',
          handleTagEvent('module telechargement', 'clic telechargement', window.location.pathname, userPref)
        );
      });
    }
    if (this._embedTweet) {
      this._embedTweet.forEach(tweet => {
        tweet.removeEventListener(
          'click',
          handleTagEvent('module social', 'clic publication twitter', window.location.pathname, userPref)
        );
      });
    }
    if (this._embedFacebook) {
      this._embedFacebook.forEach(tweet => {
        tweet.removeEventListener(
          'click',
          handleTagEvent('module social', 'clic publication facebook', window.location.pathname, userPref)
        );
      });
    }
    if (this._embedInstagram) {
      this._embedInstagram.forEach(tweet => {
        tweet.removeEventListener(
          'click',
          handleTagEvent('module social', 'clic publication instagram', window.location.pathname, userPref)
        );
      });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { userPref } = this.props;

    if (this._articleContent) {
      const downloads = Array.from(this._articleContent.getElementsByClassName('card--download'));
      downloads.forEach(download => {
        this._links = Array.from(download.getElementsByTagName('a'));
        this._links.forEach(link => {
          link.addEventListener(
            'click',
            handleTagEvent('module telechargement', 'clic telechargement', window.location.pathname, userPref)
          );
        });
      });
    }
    // TODO : change to activate click only on the card
    if (this._articleContent) {
      this._embedTweet = Array.from(this._articleContent.getElementsByClassName('is-provider-twitter'));
      this._embedTweet.forEach(tweet => {
        tweet.addEventListener(
          'click',
          handleTagEvent('module social', 'clic publication twitter', window.location.pathname, userPref)
        );
      });
    }
    if (this._articleContent) {
      this._embedFacebook = Array.from(this._articleContent.getElementsByClassName('is-provider-facebook'));
      this._embedFacebook.forEach(tweet => {
        tweet.addEventListener(
          'click',
          handleTagEvent('module social', 'clic publication facebook', window.location.pathname, userPref)
        );
      });
    }
    if (this._articleContent) {
      this._embedInstagram = Array.from(this._articleContent.getElementsByClassName('is-provider-instagram'));
      this._embedInstagram.forEach(tweet => {
        tweet.addEventListener(
          'click',
          handleTagEvent('module social', 'clic publication instagram', window.location.pathname, userPref)
        );
      });
    }
  }

  clickTagManager = (socialName: string) => {
    const { userPref } = this.props;
    const { title } = this.props.article;

    return handleTagEvent('partage', title, socialName, userPref);
  };

  renderIntroBlocs = (blocs: BlocContent[]): any => {
    if (
      blocs.length > 0 &&
      blocs[0] &&
      blocs[0].tagName &&
      blocs[0].tagName === 'div' &&
      blocs[0].attributes &&
      blocs[0].attributes[0].value === 'article__headline'
    ) {
      return <ContentBloc {...blocs[0]} />;
    }
    return '';
  };

  renderTrueBlocs = (blocs: BlocContent[]): any => {
    return blocs.map((blocFigure, index) => {
      if (
        index === 0 &&
        blocFigure.tagName &&
        blocFigure.tagName === 'div' &&
        blocFigure.attributes &&
        blocFigure.attributes[0].value === 'article__headline'
      ) {
        return null;
      }
      return <ContentBloc key={index} {...blocFigure} />;
    });
  };

  renderAuteur = (auteur: any): any => {
    if (!auteur.author_link) return null;
    const logo =
      !auteur.avatar_urls || !auteur.avatar_urls[96] ? '/img/placeholder-auteur.png' : auteur.avatar_urls[96];
    return (
      <a href={auteur.author_link} className="block-auteur" itemScope itemType="https://schema.org/Person">
        <div className="block-auteur__profile" style={{ backgroundImage: 'url(' + logo + ')' }}>
          <img src={logo} alt="" itemProp="image" className="block-auteur__profile" />
        </div>
        <div>
          <span className="link-competition__title ft-h4" itemProp="name">
            {auteur.nom}
          </span>
          <span className="link-competition__empty" itemProp="jobTitle">
            {auteur.role_edition}
          </span>
        </div>
      </a>
    );
  };

  render() {
    const {
      article: {
        label,
        caption,
        title,
        illustration,
        illustration_video,
        blocs,
        tags,
        sidebar,
        auteur,
        similar_content
      },
      userPref
    } = this.props;
    const date = getDateFromString(this.props.article.date);
    if (this.props.status === STATUS_UNKNOWN) {
      return <PageNotFound />;
    }

    const {
      status,
      article: { redirection }
    } = this.props;
    if (status === STATUS_SUCCESS && redirection) {
      return <Redirect to={{ pathname: redirection }} />;
    }

    if (blocs) {
      return (
        <div className="container">
          <div className="row">
            <div className="col col-md-8 col-lg-9 bg-overflow">
              <section>
                <div className="col col-lg-8on9 mb-3">
                  <Breadcrumb
                    breadcrumbStyle="mt-3"
                    elements={[
                      { name: 'accueil', to: '/' },
                      { name: 'actualites', to: `/actualites` },
                      { name: label.name, to: `/actualites/${label.slug}` },
                      title
                    ]}
                  />
                </div>

                <article className="row article clearfix">
                  <ArticleHeader
                    date={date}
                    imgCaption={caption}
                    label={label.name}
                    title={title}
                    imgLink={illustration}
                    video={illustration_video}
                  />

                  <div className="col col-lg-7on9 col-lg-offset-1on9" ref={r => (this._articleContent = r)}>
                    {this.props.match.params.slug === 'test-join-stories' && (
                      <JoinStories
                        styles={{ margin: '5px 2px', minHeight: '81 px' }}
                        scriptSrc="https://ffr.my.join-stories.com/widgets/test-aXq/index.js"
                        scriptWidgetId="list-bubble-test-aXq"
                        scriptWidgetAlias="test-aXq"
                      />
                    )}

                    {this.renderIntroBlocs(blocs)}

                    <ArticleSocial
                      url={window.location.href}
                      title={this.props.article ? this.props.article.title : ''}
                      userPref={userPref}
                    />
                    {this.renderTrueBlocs(blocs)}
                    {this.renderAuteur(auteur)}
                  </div>
                </article>
                <div className="col col-lg-7on9 col-lg-offset-1on9">
                  {tags.length > 0 && <ArticleTags tags={tags} />}
                  <FooterSocial
                    url={window.location.href}
                    title={this.props.article ? this.props.article.title : ''}
                    titleclass="mt-md-3 mt-lg-5 mb-3 ft-400"
                    clickTagManager={socialName => this.clickTagManager(socialName)}
                  />
                  {similar_content && !!similar_content.length && <SimilarContent similarContent={similar_content} />}
                </div>
              </section>
            </div>
            <Sidebar article={true} sidebarContent={sidebar} />
          </div>
        </div>
      );
    }

    return (
      <div className="container">
        <div className="row">
          <div className="col col-md-8 col-lg-9 bg-overflow">
            <section>
              <div className="row">
                <div className="col col-lg-8on9 mb-3">
                  <Breadcrumb
                    breadcrumbStyle="mt-3"
                    elements={[
                      { name: 'accueil', to: '/' },
                      { name: 'actualites', to: `/actualites` },
                      { name: label.name, to: `/actualites/${label.slug}` },
                      title
                    ]}
                  />
                </div>
                <article className="row article clearfix">
                  <ArticleHeader
                    date={date}
                    imgCaption={caption}
                    label={label.name}
                    title={title}
                    imgLink={illustration}
                    video={illustration_video}
                  />
                  <div className="col col-lg-7on9 col-lg-offset-1on9">
                    <ArticleSocial userPref={userPref} />
                    {this.renderAuteur(auteur)}
                  </div>
                </article>
                <div className="col col-lg-7on9 col-lg-offset-1on9">
                  <ArticleTags tags={tags} />
                  <h3 className="mt-md-3 mt-lg-5 mb-3 ft-400">Partager</h3>
                  <FooterSocial clickTagManager={this.clickTagManager} />
                  {similar_content && !!similar_content.length && <SimilarContent similarContent={similar_content} />}
                </div>
              </div>
            </section>
          </div>
          <Sidebar article={true} />
        </div>
      </div>
    );
  }
}

export default (withHelmet(Article)((Helmet, pageProps) => {
  const {
    article: { tags: propsTags, title, meta_description, meta_noindex, illustration }
  } = pageProps;
  let tags = [];
  propsTags.forEach(tag => {
    tags.push(tag.name);
  });

  if (!!meta_noindex) {
    return (
      <Helmet>
        <meta name="noindex" />
      </Helmet>
    );
  }

  return (
    <Helmet>
      <title>{title}</title>
      <link rel={'canonical'} href={window.location.href} />
      <link rel={'amphtml'} href={makeAmpLink(pageProps.article.slug)} />
      <meta name={'description'} content={meta_description} />
      <meta name={'keywords'} content={tags.join(', ')} />
      <meta property={'og:url'} content={window.location.href} />
      <meta property={'og:title'} content={title} />
      <meta property={'og:description'} content={meta_description} />
      <meta property={'og:image'} content={illustration} />
      <meta property={'og:image:url'} content={illustration} />
      {pageProps.match.params.slug === 'test-join-stories' && (
        <script
          src="https://ffr.my.join-stories.com/widgets/test-aXq/index.js"
          data-join-widget-id="list-bubble-test-aXq"
          data-join-widget-alias="test-aXq"
          type="text/javascript"
        ></script>
      )}
    </Helmet>
  );
}): any);
